import { Fragment, useCallback } from 'react';

import type { GridApi } from '@mint-lib/ag-grid/community';
import {
  Button,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { FileSystemNode } from '../../../../types/fileSystem.js';
import { dropZoneClasses, useFolderDropZone } from '../dnd/dndContext.js';
import { useGridRowDropZone } from '../dnd/useGridRowDropZone.js';

type Breadcrumb = {
  folderId: string;
  name: string;
};

type Props = {
  items: Breadcrumb[];
  onFolderChange: (id: string) => void;
  /** Present only while the table view is mounted. */
  gridApi: GridApi<FileSystemNode> | null;
};

/**
 * Crumbs double as drop targets, so a node can be moved up a level without
 * navigating there first. Tiles arrive through dnd-kit and table rows through
 * AG Grid's external drop zone, so each crumb registers with both.
 */
const BreadcrumbButton = ({
  item,
  isLast,
  onFolderChange,
  gridApi,
}: {
  item: Breadcrumb;
  isLast: boolean;
  onFolderChange: (id: string) => void;
  gridApi: GridApi<FileSystemNode> | null;
}) => {
  const dnd = useFolderDropZone(item.folderId);
  const grid = useGridRowDropZone(item.folderId, gridApi);

  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      dnd.setDropRef(element);
      grid.setGridDropRef(element);
    },
    [dnd.setDropRef, grid.setGridDropRef],
  );

  const isDropAllowed = dnd.isDropAllowed || grid.isDropAllowed;
  const isDropRejected = dnd.isDropRejected || grid.isDropRejected;

  return (
    <div ref={setRefs} className="rounded-sm">
      <Button
        variant={'text'}
        className={cn(
          'uppercase hover:bg-transparent',
          { 'disabled:text-text-primary': isLast },
          dropZoneClasses({ isDropAllowed, isDropRejected }),
        )}
        onClick={isLast ? undefined : () => onFolderChange(item.folderId)}
        disabled={isLast}
      >
        <TruncateText>{item.name}</TruncateText>
      </Button>
    </div>
  );
};

const FileSystemBreadcrumbs = ({ items, onFolderChange, gridApi }: Props) => {
  return (
    <div className="flex items-center justify-start gap-0">
      {items.map((item, index) => {
        const last = index === items.length - 1;

        return (
          <Fragment key={item.folderId}>
            <BreadcrumbButton
              item={item}
              isLast={last}
              onFolderChange={onFolderChange}
              gridApi={gridApi}
            />

            {!last && (
              <p
                className={typographyVariants({
                  variant: 'h5',
                })}
              >
                /
              </p>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export { FileSystemBreadcrumbs };
